import storage from 'redux-persist/lib/storage';
import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  createTransform,
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';

// REDUCERS
import auth, { auth as authSlice } from 'store/auth';
import registration from 'store/registration';
import dancers from 'store/dancers';
import parents from 'store/parents';
import scheduler from 'store/scheduler';
import general from 'store/general';
import createDancer from 'store/createDancer';
import dancerDetails from 'store/dancerDetails';
import createParent from 'store/createParent';
import parentDetails from 'store/parentDetails';
import classes from 'store/classes';
import createClass from 'store/createClass';
import classDetails from 'store/classDetails';
import routines from 'store/routines';
import createRoutine from 'store/createRoutine';
import routineDetails from 'store/routineDetails';
import events from 'store/events';
import createEvent from 'store/createEvent';
import eventDetails from 'store/eventDetails';
import createPerformance from 'store/createPerformance';
import performanceDetails from 'store/performanceDetails';
import configuration from 'store/configuration';
import mediaDetails from 'store/mediaDetails';
import media from 'store/media';
import invoices from 'store/invoices';
import createInvoice from 'store/createInvoice';
import team from 'store/team';
import profile from 'store/profile';
import dashboard from 'store/dashboard';
import notes from 'store/notes';

const appReducer = combineReducers({
  auth,
  registration,
  dancers,
  parents,
  scheduler,
  general,
  createDancer,
  dancerDetails,
  createParent,
  parentDetails,
  classes,
  createClass,
  classDetails,
  routines,
  createRoutine,
  routineDetails,
  events,
  team,
  createEvent,
  eventDetails,
  createPerformance,
  performanceDetails,
  configuration,
  media,
  mediaDetails,
  invoices,
  createInvoice,
  profile,
  dashboard,
  notes,
});

const rootReducer = (state, action) =>
  appReducer(
    action.type === authSlice.thunks.logout.fulfilled.type ? undefined : state,
    action
  );

const normalizePersistedStateField = (formInfo) => {
  if (!formInfo?.state || typeof formInfo.state !== 'object') {
    return formInfo;
  }

  return {
    ...formInfo,
    state: formInfo.state.label || '',
  };
};

const stripRelationshipDrafts = createTransform(
  (inboundState, key) => {
    if (key === 'createDancer') {
      return { ...inboundState, guardians: [] };
    }

    if (key === 'createParent') {
      return { ...inboundState, dancers: [] };
    }

    return inboundState;
  },
  (outboundState, key) => {
    if (!outboundState) {
      return outboundState;
    }

    if (key === 'createDancer') {
      return { ...outboundState, guardians: [] };
    }

    if (key === 'createParent') {
      return { ...outboundState, dancers: [] };
    }

    return outboundState;
  },
  { whitelist: ['createDancer', 'createParent'] }
);

const persistConfig = {
  key: 'studio manager',
  version: 1,
  storage,
  transforms: [stripRelationshipDrafts],
  migrate: (state) => {
    if (!state) {
      return Promise.resolve(state);
    }

    return Promise.resolve({
      ...state,
      createDancer: state.createDancer
        ? {
            ...state.createDancer,
            defaultDancerInfo: normalizePersistedStateField(
              state.createDancer.defaultDancerInfo
            ),
          }
        : state.createDancer,
      createParent: state.createParent
        ? {
            ...state.createParent,
            defaultParentInfo: normalizePersistedStateField(
              state.createParent.defaultParentInfo
            ),
          }
        : state.createParent,
    });
  },
  blacklist: [],
  whitelist: [
    'auth',
    'registration',
    'general',
    'createDancer',
    'createParent',
    'createClass',
    'createRoutine',
    // 'createEvent', need to add ???
    'createPerformance',
  ],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  devTools: process.env.NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export default store;
